/**
 * App.tsx — Root application component for billing-admin
 *
 * Purpose:
 *   Composes the top-level provider tree and router for the billing admin
 *   console widget. Wires QueryClientProvider, AdminAuthProvider, and
 *   BrowserRouter together, then defines the application routes including
 *   OIDC login/callback pages and all protected admin navigation sections.
 *
 * Architecture Context:
 *   H4=embedded: the host supplies an AdminConfig (apiBaseUrl) via mount().
 *   Admin authentication uses rule42-auth OIDC Authorization Code + PKCE.
 *   Direct entry on finance.rule42.ai and bootstrap token handoff from
 *   rule42-cloud both hydrate the same auth context. AdminAuthGuard protects
 *   all routes except /login and /auth/callback.
 *
 *   H3 binding: SettingsPage (ReconciliationPage repurposed as settings).
 *   A dedicated settings panel displays the active PDF_STORAGE_BACKEND value.
 *
 * @since 2026-05-31 (AT-50.01 — Scaffold Admin Console UI Foundation)
 */
import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import type { AdminConfig } from './index';
import { AdminAuthProvider } from './contexts/AdminAuthContext';
import { OrgContextProvider } from './contexts/OrgContext';
import { GuideProvider } from './components/IntegrationGuide';
import AdminAuthGuard from './guards/AdminAuthGuard';
import OverviewPage from './pages/OverviewPage';
import LoginPage from './pages/LoginPage';
import AuthCallbackPage from './pages/AuthCallbackPage';
import OrgSelectorPage from './pages/OrgSelectorPage';
import OrgEnrollmentPage from './pages/OrgEnrollmentPage';
import CustomersPage from './pages/CustomersPage';
import BillingConfigPage from './pages/BillingConfigPage';
import PlansPage from './pages/PlansPage';
import SubscriptionsPage from './pages/SubscriptionsPage';
import InvoicesPage from './pages/InvoicesPage';
import RevenueDashboardPage from './pages/RevenueDashboardPage';
import EnterpriseContractsPage from './pages/EnterpriseContractsPage';
import OperatorsPage from './pages/OperatorsPage';
import AuditLogPage from './pages/AuditLogPage';
import ReconciliationPage from './pages/ReconciliationPage';
import CustomerDetailPage from './pages/CustomerDetailPage';
import UsagePricingPage from './pages/UsagePricingPage';
import UsageEventLogPage from './pages/UsageEventLogPage';
import EventTypesPage from './pages/EventTypesPage';
import CreditsDashboardPage from './pages/CreditsDashboardPage';
import UserBudgetsPage from './pages/UserBudgetsPage';
import PricingPage from './pages/PricingPage';
import WebhooksPage from './pages/WebhooksPage';
import SettingsPage from './pages/SettingsPage';
import OrgSettingsPage from './pages/OrgSettingsPage';
import MembersPage from './pages/MembersPage';
import ApplicationsPage from './pages/ApplicationsPage';
import PlatformDashboardPage from './pages/PlatformDashboardPage';
import PlatformRevenuePage from './pages/PlatformRevenuePage';
import PlatformSettingsPage from './pages/PlatformSettingsPage';
import PlatformHealthPage from './pages/PlatformHealthPage';
import TenantsListPage from './pages/TenantsListPage';
import EmailTemplatesPage from './pages/EmailTemplatesPage';
import ByodConfigPage from './pages/ByodConfigPage';
import WebhookEventBrowserPage from './pages/WebhookEventBrowserPage';
import IntegrationDocsPage from './pages/IntegrationDocsPage';
import ProviderOnboardingPage from './pages/ProviderOnboardingPage';
import AutomationRulesPage from './pages/AutomationRulesPage';
import EnforcementSetupPage from './pages/EnforcementSetupPage';
import MarketingFeaturesPage from './pages/MarketingFeaturesPage';
import PricingPagesPage from './pages/PricingPagesPage';
import McpServerPage from './pages/McpServerPage';

// ---------------------------------------------------------------------------
// QueryClient singleton
// ---------------------------------------------------------------------------
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // RATIONALE: 1-retry policy balances resilience vs. user wait time
      retry: 1,
      staleTime: 30_000,
    },
  },
});

// ---------------------------------------------------------------------------
// App
// ---------------------------------------------------------------------------
interface AppProps {
  /** Runtime configuration supplied by the host at mount time. */
  config: AdminConfig;
}

/**
 * App — root component that sets up the full provider + router tree.
 *
 * @param props.config - Host-supplied admin configuration
 * @since 2026-05-31 (AT-50.01)
 */
export default function App({ config }: AppProps): React.JSX.Element {
  return (
    <QueryClientProvider client={queryClient}>
      <AdminAuthProvider apiBaseUrl={config.apiBaseUrl} basename={config.basename} auth={config.auth}>
        <OrgContextProvider>
        <GuideProvider>
        <BrowserRouter basename={config.basename}>
          <Routes>
            {/* Public routes — no guard */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/auth/callback" element={<AuthCallbackPage />} />
            <Route path="/enroll" element={<OrgEnrollmentPage />} />

            {/* Protected routes — AdminAuthGuard redirects to /login if not authenticated */}
            <Route element={<AdminAuthGuard />}>
              {/* Org selector for multi-org users */}
              <Route path="/select-org" element={<OrgSelectorPage />} />

              {/* Platform admin routes (PlatformLayout handles its own access control) */}
              <Route path="/platform/dashboard" element={<PlatformDashboardPage />} />
              <Route path="/platform/tenants" element={<TenantsListPage />} />
              <Route path="/platform/enroll" element={<OrgEnrollmentPage />} />
              <Route path="/platform/revenue" element={<PlatformRevenuePage />} />
              <Route path="/platform/settings" element={<PlatformSettingsPage />} />
              <Route path="/platform/settings/:category" element={<PlatformSettingsPage />} />
              <Route path="/platform/health" element={<PlatformHealthPage />} />
              <Route path="/platform/audit-log" element={<AuditLogPage />} />

              {/* Backward-compatible flat routes (existing pages — org context from JWT) */}
              <Route path="/" element={<Navigate to="/overview" replace />} />
              <Route path="/overview" element={<OverviewPage />} />
              <Route path="/customers" element={<CustomersPage />} />
              <Route path="/customers/:id" element={<CustomerDetailPage />} />
              <Route path="/products" element={<Navigate to="/plans" replace />} />
              <Route path="/billing-config" element={<BillingConfigPage />} />
              <Route path="/plans" element={<PlansPage />} />
              <Route path="/usage-pricing" element={<UsagePricingPage />} />
              <Route path="/usage-events" element={<UsageEventLogPage />} />
              <Route path="/event-types" element={<EventTypesPage />} />
              <Route path="/credits" element={<CreditsDashboardPage />} />
              <Route path="/user-budgets" element={<UserBudgetsPage />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/subscriptions" element={<SubscriptionsPage />} />
              <Route path="/invoices" element={<InvoicesPage />} />
              <Route path="/revenue" element={<RevenueDashboardPage />} />
              <Route path="/contracts" element={<EnterpriseContractsPage />} />
              <Route path="/operators" element={<OperatorsPage />} />
              <Route path="/audit-log" element={<AuditLogPage />} />
              <Route path="/webhooks" element={<WebhooksPage />} />
              <Route path="/reconciliation" element={<ReconciliationPage />} />
              <Route path="/settings" element={<OrgSettingsPage />} />

              {/* New SaaS pages */}
              <Route path="/members" element={<MembersPage />} />
              <Route path="/applications" element={<ApplicationsPage />} />
              <Route path="/email-templates" element={<EmailTemplatesPage />} />
              <Route path="/byod" element={<ByodConfigPage />} />
              <Route path="/webhook-events" element={<WebhookEventBrowserPage />} />
              <Route path="/integration-docs" element={<IntegrationDocsPage />} />
              <Route path="/provider" element={<ProviderOnboardingPage />} />
              <Route path="/automation-rules" element={<AutomationRulesPage />} />
              <Route path="/enforcement-setup" element={<EnforcementSetupPage />} />
              <Route path="/marketing-features" element={<MarketingFeaturesPage />} />
              <Route path="/pricing-pages" element={<PricingPagesPage />} />
              <Route path="/mcp-server" element={<McpServerPage />} />

              {/* Org-scoped routes (explicit org in URL) */}
              <Route path="/org/:orgId/overview" element={<OverviewPage />} />
              <Route path="/org/:orgId/customers" element={<CustomersPage />} />
              <Route path="/org/:orgId/plans" element={<PlansPage />} />
              <Route path="/org/:orgId/subscriptions" element={<SubscriptionsPage />} />
              <Route path="/org/:orgId/invoices" element={<InvoicesPage />} />
              <Route path="/org/:orgId/credits" element={<CreditsDashboardPage />} />
              <Route path="/org/:orgId/members" element={<MembersPage />} />
              <Route path="/org/:orgId/applications" element={<ApplicationsPage />} />
              <Route path="/org/:orgId/settings" element={<OrgSettingsPage />} />
            </Route>

            {/* Catch-all redirect */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        </GuideProvider>
        </OrgContextProvider>
      </AdminAuthProvider>
    </QueryClientProvider>
  );
}
